import { Anchor, Box, Group, Stack } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { ButtonDetails } from "@/components/controls"
import { SessionLink } from "@/components/sessions"
import { openModalPeerDetails } from "@/components/surveys/details/ModalPeerDetails"
import { AdminOnly, AppPagination, AppTitle, DateTime, icons, Score } from "@/components/utils"
import { useAPIResources, useAppPagination, usePermissions } from "@/hooks"
import type { TSession, TUser } from "@/types"
import { buildPeerReviewURL, fetchAPIFeedbackScores, sessionsAPI, t } from "@/utils"
import { BlockSkeleton, Panel } from "./utils"

export function ProfileFeedbacks({ user }: { user: TUser }) {
  const { isAdmin, isManager, user: curuser } = usePermissions()
  const hasPermissions = isAdmin || curuser?.id === user.id

  const where: any = {
    hidden: false,
    type: "feedback",
    peer_id: user.id,
    visibility: hasPermissions ? undefined : isManager ? { $in: ["managers", "public"] } : { $in: ["public"] },
  }
  if (!isAdmin) where.has_peer_report = true

  const { data: { data = [] } = {}, isPending } = useAPIResources(sessionsAPI, {
    where,
    sort: ["-dtstart"],
  })

  const pagination = useAppPagination(5)
  const total = data?.length || 0
  useEffect(() => {
    pagination.setTotal(total)
  }, [total])
  const pageData = data?.slice(pagination.offset, pagination.offset + pagination.limit) || []
  const sessionIds = pageData.map((session) => session.id)
  const { data: scores = [] } = useQuery({
    enabled: !!pageData?.length,
    queryKey: ["feedback-scores", user.id, sessionIds],
    queryFn: () =>
      fetchAPIFeedbackScores({
        where: { peer_id: user.id, session_id: { $in: sessionIds } },
      }),
  })

  if (!isPending && !pageData?.length) return null

  return (
    <Panel
      block={!isPending}
      sm={{
        gridColumnEnd: "span 2",
      }}
      xl={{
        order: 1,
        gridColumnStart: 0,
      }}
    >
      {isPending ? (
        <BlockSkeleton />
      ) : (
        <Stack gap="sm">
          <AppTitle>
            <icons.feedback />
            {t("Team Feedback")}
          </AppTitle>
          {pageData?.map((session) => (
            <SessionRow
              peer={user}
              key={session.id}
              session={session}
              score={scores?.find((score) => score.session_id === session.id)}
            />
          ))}
          <AppPagination {...pagination} />
        </Stack>
      )}
    </Panel>
  )
}

function SessionRow({ session, peer, score }: { session: TSession; peer: TUser; score?: { score: number | null } }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <AdminOnly fallback={() => <Anchor href={buildPeerReviewURL(session, peer.id)}>{session.title}</Anchor>}>
        <SessionLink session={session}>{session.title}</SessionLink>
      </AdminOnly>
      <Box ml="auto" />
      {score?.score !== undefined && score?.score !== null && <Score text value={score.score} size="md" />}
      <DateTime timestamp={session.dtstart} format="L" />
      <ButtonDetails
        flex="0 0 auto"
        onClick={() =>
          openModalPeerDetails({
            peer,
            session,
          })
        }
      />
    </Group>
  )
}
